import type { Params } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'
import { CustomPeriodsPageDocument, type CustomPeriodsPageQuery } from 'generated/resources/graphql'
import moment from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'
import { range } from 'modules/core/lib/frequencies'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import CustomPeriodForm, {
  CUSTOM_PERIOD_TYPES,
} from 'modules/attendance/components/TimeTracking/Layout/form'
import TimeTrackingCollection from 'modules/attendance/components/TimeTrackingCollection'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { TimeTrackingViewType } from 'modules/attendance/lib/useCustomPeriodPath'

import useFetchCustomPeriods from './useFetchCustomPeriods'

export type ClockInStatus = NonNullable<
  CustomPeriodsPageQuery['timeInsights']
>['clockInStatusesConnection']['nodes'][number]

function periodFromParams(params: Readonly<Params<string>>): moment.Moment {
  const { year, month, day } = params
  const date = moment([Number(year), Number(month) - 1, Number(day)])

  return date.isValid() ? date : moment()
}

const CustomPeriodsPage = () => {
  // TODO: move calculation of [from, to] into custom period forms and the params too,
  // Reason: so we can have a single source of truth for this information
  // and not fetching the dates both from custom field and params
  const params = useParams()
  const dateFromParams = periodFromParams(params)
  const { periodType = '' } = params
  const isoWeekly = true
  const today = moment()
  const [from = today, to = today] = range(dateFromParams, periodType, isoWeekly)
  const [viewType, setViewType] = useLocalStorage<TimeTrackingViewType>(
    LOCAL_STORAGE_KEYS.timeTrackingViewType
  )

  // Always use the time tracking collection (legacy path removed).
  const skipLegacyTimeTracking = true

  const customPeriodForm = useLazyRef(
    () =>
      new CustomPeriodForm({
        period_type: periodType,
        start_on: dateToString(from, 'backend'),
        end_on: dateToString(to, 'backend'),
      })
  ).current

  useEffect(() => {
    const fromMonth = from.month() + 1
    const hasViewTypeChanged = viewType?.periodType !== periodType || viewType.month !== fromMonth

    if (hasViewTypeChanged) {
      setViewType({
        periodType,
        year: from.year(),
        month: fromMonth,
        day: from.date(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType])

  const { loading: _loadingCustomPeriods } = useFetchCustomPeriods(
    customPeriodForm,
    periodType,
    skipLegacyTimeTracking
  )

  useAttendanceFeatures(Features.timeoff_overtime_compensation)

  const { featureAvailability: hasRealTimeAttendance } = useAttendanceFeatures(
    Features.time_tracking_real_time_attendance
  )

  const isDailyTab = hasRealTimeAttendance && periodType === CUSTOM_PERIOD_TYPES.daily
  const { year, month, day } = useParams()
  const now = new Date()
  const isToday =
    Number(year) == now.getFullYear() &&
    Number(month) == now.getMonth() + 1 &&
    Number(day) == now.getDate()
  const canSendEmailReminder = isDailyTab && isToday

  useQuery(CustomPeriodsPageDocument, {
    variables: {
      endOn: dateToString(moment(), 'backend'),
      startOn: dateToString(moment(), 'backend'),
      includeClockInStatuses: hasRealTimeAttendance && canSendEmailReminder,
    },
    skip: skipLegacyTimeTracking,
  })

  return <TimeTrackingCollection />
}

export default CustomPeriodsPage
